const DEFAULT_BASE_URL =
  process.env.REACT_APP_ODOO_URL || "https://bob-services-ship.odoo.com";

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 8000;
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffDelayMs(attempt, baseDelayMs, maxDelayMs) {
  const exponential = baseDelayMs * 2 ** attempt;
  const jitter = Math.random() * baseDelayMs;
  return Math.min(exponential + jitter, maxDelayMs);
}

export class OdooStatementError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "OdooStatementError";
    this.status = status;
    this.payload = payload;
  }
}

export class OdooStatementClient {
  constructor(apiKey, baseUrl = DEFAULT_BASE_URL, retryOptions = {}) {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
    this.retryOptions = retryOptions;
  }

  async get(path) {
    const {
      maxRetries = DEFAULT_MAX_RETRIES,
      baseDelayMs = DEFAULT_BASE_DELAY_MS,
      maxDelayMs = DEFAULT_MAX_DELAY_MS,
    } = this.retryOptions;

    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        return await this.fetchJson(path);
      } catch (error) {
        lastError = error;
        const retryable =
          error instanceof TypeError ||
          (error instanceof OdooStatementError && RETRYABLE_STATUS.has(error.status));
        if (attempt >= maxRetries || !retryable) throw error;
        await sleep(backoffDelayMs(attempt, baseDelayMs, maxDelayMs));
      }
    }
    throw lastError;
  }

  async fetchJson(path) {
    const response = await fetch(`${this.baseUrl.replace(/\/$/, "")}${path}`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${this.apiKey}`,
      },
    });
    const payload = await response.json();
    if (!response.ok) {
      const message = typeof payload?.error === "string" ? payload.error : `HTTP ${response.status}`;
      throw new OdooStatementError(message, response.status, payload);
    }
    return payload;
  }

  getSummary() {
    return this.get("/api/statement/summary");
  }

  getDashboardSummary() {
    return this.get("/api/dashboard/summary");
  }

  getLines(params = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set("date_from", params.dateFrom);
    if (params.dateTo) query.set("date_to", params.dateTo);
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.get(`/api/statement/lines${suffix}`);
  }

  getInvoices(params) {
    return this.getList("/api/invoices", params);
  }

  getSalesOrders(params) {
    return this.getList("/api/sales-orders", params);
  }

  getSalesOrder(orderId) {
    return this.get(`/api/sales-orders/${orderId}`);
  }

  getList(path, params = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set("date_from", params.dateFrom);
    if (params.dateTo) query.set("date_to", params.dateTo);
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.get(`${path}${suffix}`);
  }
}
